import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { cn } from "../cn";
import styles from "./Toast.module.scss";

/**
 * Minimal toast system.
 *   <ToastProvider> at the app root, then `const toast = useToast();`
 *   toast.success("Saved"), toast.error("Failed"), toast.show({ ... }).
 * Top-right viewport, ambient shadow, auto-dismiss.
 */
const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    ({ title, description, variant = "info", duration = 4000 }) => {
      const id = ++idRef.current;
      setToasts((list) => [...list, { id, title, description, variant }]);
      if (duration > 0) setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss]
  );

  const api = useMemo(
    () => ({
      show,
      dismiss,
      info: (title, opts) => show({ title, variant: "info", ...opts }),
      success: (title, opts) => show({ title, variant: "success", ...opts }),
      warning: (title, opts) => show({ title, variant: "warning", ...opts }),
      error: (title, opts) => show({ title, variant: "error", ...opts }),
    }),
    [show, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {ReactDOM.createPortal(
        <div className={styles.viewport}>
          {toasts.map((t) => (
            <div key={t.id} className={cn(styles.toast, styles[t.variant])}>
              <div className={styles.content}>
                {t.title ? <div className={styles.title}>{t.title}</div> : null}
                {t.description ? (
                  <div className={styles.description}>{t.description}</div>
                ) : null}
              </div>
              <button
                type="button"
                className={styles.close}
                aria-label="Dismiss"
                onClick={() => dismiss(t.id)}
              >
                ×
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

ToastProvider.propTypes = { children: PropTypes.node };

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}

export default ToastProvider;
